import { ref } from 'vue';
import { useApi, useToaster, type Urgency } from '@shopexperts/shared';

export interface SubmitLeadPayload {
  directThreadId?: string;
  storeId: number;
  projectTitle: string;
  projectBrief: string;
  urgency: Urgency;
  projectValueRangeId?: number;
  matchingStrategy: 'selected' | 'selected-auto' | 'auto';
  selectedExpertId?: number;
  /** ID of a packaged service offered by the selected expert */
  selectedPackagedServiceId?: number;
}

export interface SubmitLeadResult {
  success: boolean;
}

/**
 * Composable for submitting a lead.
 * 
 * Handles the API call to submit a new lead and shows
 * appropriate success/error toasts.
 * 
 * @param options - Configuration options
 * @param options.showToast - Whether to show success/error toasts (default: true)
 * 
 * @example
 * ```ts
 * const { submitLead, isSubmitting } = useSubmitLead();
 * 
 * await submitLead({
 *   storeId: 123,
 *   projectTitle: 'My Project',
 *   projectBrief: 'Description...',
 *   urgency: 'within_the_next_week',
 *   matchingStrategy: 'auto',
 * });
 * ```
 */
export function useSubmitLead(options: { showToast?: boolean } = {}) {
  const { showToast = true } = options;
  const api = useApi();
  const toaster = useToaster();

  const isSubmitting = ref(false);

  async function submitLead(payload: SubmitLeadPayload): Promise<SubmitLeadResult> {
    isSubmitting.value = true;

    try {
      await api.client.leadsV2.submitLead({
        body: {
          ...(payload.directThreadId ? { direct_thread_id: payload.directThreadId } : {}),
          store_id: payload.storeId,
          project_title: payload.projectTitle,
          project_brief: payload.projectBrief,
          urgency: payload.urgency,
          project_value_range_id: payload.projectValueRangeId,
          matching_strategy: payload.matchingStrategy,
          selected_expert_id: payload.selectedExpertId,
          selected_packaged_service_id: payload.selectedPackagedServiceId,
        },
      });

      if (showToast) {
        toaster.create({
          title: 'Lead submitted!',
          description: 'Your project lead has been submitted successfully. We\'ll match you with the right expert soon.',
          type: 'success',
        });
      }

      return { success: true };
    } catch (error: any) {
      console.error('[useSubmitLead] Submit error:', error);
      
      if (showToast) {
        toaster.create({
          title: 'Submission failed',
          description: error?.response?.data?.message || 'Failed to submit your lead. Please try again.',
          type: 'error',
        });
      }

      throw error;
    } finally {
      isSubmitting.value = false;
    }
  }

  return {
    submitLead,
    isSubmitting,
  };
}

